import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import '../styles/midnight-lab.css';

const achievements = [
    {
      id: 1,
      game: 'Decode',
      season: '2024-25',
      type: 'Competition',
      title: 'State Qualifier',
      event: 'Virginia State Championship',
      placement: '12th Place',
      description: 'Qualified for state championship through regional performance',
      date: '2025-03-15'
    },
    {
      id: 2,
      game: 'Decode',
      season: '2024-25',
      type: 'Award',
      title: 'Design Award',
      event: 'Regional Qualifier #1',
      placement: 'Winner',
      description: 'Recognized for outstanding robot design and engineering process',
      date: '2025-02-10'
    },
    {
      id: 3,
      game: 'Decode',
      season: '2024-25',
      type: 'Competition',
      title: 'Regional Finalist',
      event: 'Regional Qualifier #1',
      placement: '3rd Place',
      description: 'Strong performance in regional competition',
      date: '2025-02-10'
    },
    {
      id: 4,
      game: 'Decode',
      season: '2024-25',
      type: 'Competition',
      title: 'Regional Qualifier',
      event: 'Regional Qualifier #2',
      placement: '5th Place',
      description: 'Consistent performance across multiple events',
      date: '2025-02-24'
    },
    {
      id: 5,
      game: 'Into the Deep',
      season: '2023-24',
      type: 'Award',
      title: 'Inspire Award',
      event: 'Regional Championship',
      placement: 'Winner',
      description: 'Recognized for team spirit, outreach, and community impact',
      date: '2024-03-20'
    },
    {
      id: 6,
      game: 'Into the Deep',
      season: '2023-24',
      type: 'Competition',
      title: 'Regional Finalist',
      event: 'Regional Championship',
      placement: '8th Place',
      description: 'Advanced to regional championship finals',
      date: '2024-03-20'
    },
    {
      id: 7,
      game: 'Into the Deep',
      season: '2023-24',
      type: 'Outreach',
      title: 'Community Impact',
      event: 'STEM Fair',
      placement: 'Featured',
      description: 'Demonstrated robotics to 500+ students at local STEM fair',
      date: '2024-02-15'
    },
    {
      id: 8,
      game: 'Center Stage',
      season: '2022-23',
      type: 'Award',
      title: 'Think Award',
      event: 'Regional Qualifier',
      placement: 'Winner',
      description: 'Awarded for outstanding engineering notebook and documentation',
      date: '2023-02-18'
    },
    {
      id: 9,
      game: 'Center Stage',
      season: '2022-23',
      type: 'Competition',
      title: 'Qualifier Finalist',
      event: 'Regional Qualifier #1',
      placement: '4th Place',
      description: 'Strong showing in early season competition',
      date: '2023-02-18'
    },
    {
      id: 10,
      game: 'Center Stage',
      season: '2022-23',
      type: 'Outreach',
      title: 'Mentorship Program',
      event: 'Elementary School',
      placement: 'Featured',
      description: 'Mentored FLL team and organized robotics workshop',
      date: '2023-01-10'
    },
    {
      id: 11,
      game: 'Powerplay',
      season: '2021-22',
      type: 'Competition',
      title: 'Regional Qualifier',
      event: 'Regional Qualifier #1',
      placement: '6th Place',
      description: 'Strong performance in our first competitive season',
      date: '2022-02-12'
    },
    {
      id: 12,
      game: 'Powerplay',
      season: '2021-22',
      type: 'Outreach',
      title: 'School Demo',
      event: 'Local High School',
      placement: 'Featured',
      description: 'Introduced robotics program to school community',
      date: '2022-01-20'
    }
];

function FTCAchievementsPage() {
  const [selectedSeason, setSelectedSeason] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [visible, setVisible] = useState({});
  const [achievementVisible, setAchievementVisible] = useState({});
  const [countValues, setCountValues] = useState({ awards: 0, competitions: 0, outreach: 0 });
  const sectionRefs = useRef({});
  const achievementRefs = useRef({});

  const seasons = [
    { value: 'all', label: 'All Seasons' },
    { value: 'Decode', label: 'Decode (2024-25)', latest: true },
    { value: 'Into the Deep', label: 'Into the Deep (2023-24)' },
    { value: 'Center Stage', label: 'Center Stage (2022-23)' },
    { value: 'Powerplay', label: 'Powerplay (2021-22)' }
  ];

  const types = ['all', 'Competition', 'Award', 'Outreach'];

  const filteredAchievements = useMemo(() => {
    return achievements.filter(achievement => {
      const matchSeason = selectedSeason === 'all' || achievement.game === selectedSeason;
      const matchType = selectedType === 'all' || achievement.type === selectedType;
      return matchSeason && matchType;
    });
  }, [selectedSeason, selectedType]);

  // Count up animation for highlights
  useEffect(() => {
    const targets = {
      awards: achievements.filter(a => a.type === 'Award').length,
      competitions: achievements.filter(a => a.type === 'Competition').length,
      outreach: 150
    };

    const duration = 2000;
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeOut = 1 - Math.pow(1 - progress, 3);

      setCountValues({
        awards: Math.floor(targets.awards * easeOut),
        competitions: Math.floor(targets.competitions * easeOut),
        outreach: Math.floor(targets.outreach * easeOut)
      });

      if (currentStep >= steps) {
        clearInterval(interval);
        setCountValues(targets);
      }
    }, stepDuration);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const observers = Object.keys(sectionRefs.current).map((key) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.1 }
      );
    });

    Object.keys(sectionRefs.current).forEach((key, index) => {
      if (sectionRefs.current[key]) {
        observers[index].observe(sectionRefs.current[key]);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, []);

  // Staggered animation for achievement cards
  useEffect(() => {
    setAchievementVisible({}); // Reset visibility when filters change
    const filtered = filteredAchievements;
    const observers = filtered.map((achievement, index) => {
      return new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              setAchievementVisible((prev) => ({ ...prev, [achievement.id]: true }));
            }, index * 100);
          }
        },
        { threshold: 0.1 }
      );
    });

    filtered.forEach((achievement, index) => {
      const ref = achievementRefs.current[achievement.id];
      if (ref && observers[index]) {
        observers[index].observe(ref);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [filteredAchievements]);

  return (
    <div style={{ backgroundColor: 'var(--color-bg-primary)', paddingTop: '100px' }}>
      {/* Hero Section */}
      <section
        ref={(el) => (sectionRefs.current['hero'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          textAlign: 'center',
          opacity: visible.hero ? 1 : 0,
          transform: visible.hero ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out'
        }}
      >
        <h1 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(3rem, 6vw, 5rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-6)',
          letterSpacing: '-0.03em'
        }}>
          FTC Achievements
        </h1>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(1.125rem, 2vw, 1.5rem)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.7,
          maxWidth: '700px',
          margin: '0 auto'
        }}>
          Celebrating our FTC journey through competition, innovation, and community impact
        </p>
      </section>

      {/* Highlights Section */}
      <section
        ref={(el) => (sectionRefs.current['highlights'] = el)}
        style={{
          padding: 'var(--space-16) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.highlights ? 1 : 0,
          transform: visible.highlights ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 'var(--space-8)',
          borderTop: '1px solid rgba(0, 16, 17, 0.1)',
          borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
          padding: 'var(--space-12) 0'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(2.5rem, 4vw, 4rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.awards}
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Awards Won
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(2.5rem, 4vw, 4rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.competitions}
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Competitions
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'clamp(2.5rem, 4vw, 4rem)',
              fontWeight: 700,
              color: 'var(--color-frozen-lake)',
              marginBottom: 'var(--space-2)',
              transition: 'transform 0.3s ease'
            }}>
              {countValues.outreach}+
            </div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Outreach Hours
            </div>
          </div>
        </div>
      </section>

      {/* Season Selector */}
      <section
        ref={(el) => (sectionRefs.current['filters'] = el)}
        style={{
          padding: 'var(--space-16) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.filters ? 1 : 0,
          transform: visible.filters ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-8)',
          marginBottom: 'var(--space-12)'
        }}>
          {/* Season Filter */}
          <div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-4)',
              textAlign: 'center'
            }}>
              Season
            </div>
            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              {seasons.map((season) => (
                <button
                  key={season.value}
                  onClick={() => setSelectedSeason(season.value)}
                  style={{
                    padding: 'var(--space-3) var(--space-6)',
                    backgroundColor: selectedSeason === season.value ? 'var(--color-text-primary)' : 'transparent',
                    color: selectedSeason === season.value ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                    border: 'none',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)',
                    position: 'relative',
                    textTransform: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedSeason !== season.value) {
                      e.target.style.backgroundColor = 'var(--color-bg-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedSeason !== season.value) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {season.label}
                  {season.latest && (
                    <span style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      fontSize: '10px',
                      padding: '2px 6px',
                      backgroundColor: 'var(--color-frozen-lake)',
                      color: 'var(--color-text-primary)',
                      borderRadius: '10px',
                      fontWeight: 700
                    }}>
                      Latest
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div>
            <div style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              color: 'var(--color-text-secondary)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: 'var(--space-2)',
              textAlign: 'center'
            }}>
              Type
            </div>
            <div style={{
              display: 'flex',
              gap: 'var(--space-2)',
              borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
              justifyContent: 'center'
            }}>
              {types.map((type) => (
                <button
                  key={type}
                  onClick={() => setSelectedType(type)}
                  style={{
                    padding: 'var(--space-2) var(--space-4)',
                    backgroundColor: selectedType === type ? 'var(--color-text-primary)' : 'transparent',
                    color: selectedType === type ? 'var(--color-text-light)' : 'var(--color-text-primary)',
                    border: 'none',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all var(--transition-base)',
                    textTransform: 'capitalize'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedType !== type) {
                      e.target.style.backgroundColor = 'var(--color-bg-secondary)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedType !== type) {
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Achievements Grid */}
      <section
        ref={(el) => (sectionRefs.current['achievements'] = el)}
        style={{
          padding: 'var(--space-24) var(--space-4)',
          maxWidth: '1400px',
          margin: '0 auto',
          opacity: visible.achievements ? 1 : 0,
          transform: visible.achievements ? 'translateY(0)' : 'translateY(30px)',
          transition: 'opacity 0.6s ease-out, transform 0.6s ease-out'
        }}
      >
        {filteredAchievements.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: 'var(--space-16)',
            color: 'var(--color-text-secondary)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-lg)'
          }}>
            No achievements found matching your filters.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
            gap: 'var(--space-8)'
          }}>
            {filteredAchievements.map((achievement) => (
              <div
                key={achievement.id}
                ref={(el) => (achievementRefs.current[achievement.id] = el)}
                style={{
                  padding: 'var(--space-6) 0',
                  borderBottom: '1px solid rgba(0, 16, 17, 0.1)',
                  transition: 'all var(--transition-base)',
                  opacity: achievementVisible[achievement.id] ? 1 : 0,
                  transform: achievementVisible[achievement.id] 
                    ? 'translateY(0) scale(1)' 
                    : 'translateY(20px) scale(0.98)',
                  animation: achievementVisible[achievement.id] 
                    ? 'fadeInUp 0.6s ease-out forwards' 
                    : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = achievementVisible[achievement.id]
                    ? 'translateY(0) scale(1)'
                    : 'translateY(20px) scale(0.98)';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: 'var(--space-4)'
                }}>
                  <div>
                    <div style={{
                      display: 'flex',
                      gap: 'var(--space-2)',
                      alignItems: 'center',
                      marginBottom: 'var(--space-2)',
                      flexWrap: 'wrap'
                    }}>
                      <span style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {achievement.game}
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-secondary)'
                      }}>
                        •
                      </span>
                      <span style={{
                        fontFamily: 'var(--font-sans)',
                        fontSize: 'var(--text-xs)',
                        color: 'var(--color-text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {achievement.type}
                      </span>
                    </div>
                    <h3 style={{
                      fontFamily: 'var(--font-sans)',
                      fontSize: 'var(--text-2xl)',
                      fontWeight: 600,
                      color: 'var(--color-text-primary)',
                      marginBottom: 'var(--space-2)'
                    }}>
                      {achievement.title}
                    </h3>
                  </div>
                  <div style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 'var(--text-sm)',
                    fontWeight: 600,
                    color: 'var(--color-frozen-lake)',
                    textAlign: 'right'
                  }}>
                    {achievement.placement}
                  </div>
                </div>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-base)',
                  fontWeight: 500,
                  color: 'var(--color-text-primary)',
                  marginBottom: 'var(--space-2)'
                }}>
                  {achievement.event}
                </div>
                <p style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-sm)',
                  color: 'var(--color-text-secondary)',
                  lineHeight: 1.6,
                  marginBottom: 'var(--space-4)'
                }}>
                  {achievement.description}
                </p>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--color-text-secondary)'
                }}>
                  {new Date(achievement.date).toLocaleDateString('en-US', { 
                    month: 'long', 
                    day: 'numeric', 
                    year: 'numeric' 
                  })} • {achievement.season}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section style={{
        padding: 'var(--space-24) var(--space-4)',
        maxWidth: '1400px',
        margin: '0 auto',
        textAlign: 'center',
        borderTop: '1px solid rgba(0, 16, 17, 0.1)'
      }}>
        <h2 style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'clamp(2rem, 4vw, 3rem)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--space-6)',
          letterSpacing: '-0.02em'
        }}>
          Join FTC
        </h2>
        <p style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-lg)',
          color: 'var(--color-text-secondary)',
          lineHeight: 1.7,
          maxWidth: '600px',
          margin: '0 auto var(--space-8)'
        }}>
          Be part of a team that builds, competes, and makes an impact
        </p>
        <Link
          to="/join"
          style={{
            padding: '16px 40px',
            backgroundColor: 'var(--color-text-primary)',
            color: 'var(--color-text-light)',
            fontSize: 'var(--text-base)',
            fontWeight: 600,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            textDecoration: 'none',
            fontFamily: 'var(--font-sans)',
            border: '2px solid var(--color-text-primary)',
            transition: 'all var(--transition-base)',
            display: 'inline-block'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = 'var(--color-frozen-lake)';
            e.target.style.borderColor = 'var(--color-frozen-lake)';
            e.target.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'var(--color-text-primary)';
            e.target.style.borderColor = 'var(--color-text-primary)';
            e.target.style.color = 'var(--color-text-light)';
          }}
        >
          Join the Team
        </Link>
      </section>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
}

export default FTCAchievementsPage;

